import { useEffect, useState } from 'react';
import { toDataURL } from 'qrcode';

import type { components } from '../api/schema.gen';
import { ApiError, api } from '../api/client';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { isOtpComplete } from '../lib/otp';
import { useCopyToClipboard } from '../lib/useCopyToClipboard';
import { Modal } from './Modal';
import { OtpInput } from './OtpInput';
import { OtpResendPrompt } from './OtpResendPrompt';

/**
 * Begins enrolment on open (one call, no separate "start" button — opening
 * this modal already IS the start), then confirms the code the customer
 * types back. `enrollmentId` is threaded through unconditionally: confirm
 * must complete the SAME pending enrolment this modal generated, not
 * whatever attempt happens to be pending server-side.
 *
 * `method` decides the shape: totp shows a QR code and manual-entry secret
 * (a code the customer generates locally proves the setup worked); sms and
 * email_otp (LLMR-66) instead send a code immediately — the destination was
 * already proven controlled by phone verification or signup, so there is
 * nothing to scan, only a code sent to confirm the delivery pipeline itself
 * works. For SMS, callers should confirm the destination before opening this
 * modal so the send is intentional. Resending calls the same enroll endpoint
 * again — CreateOTPEnrollment on the server supersedes whatever was pending
 * before, so this doubles as enroll and resend without a separate route.
 *
 * Extracted out of EditProfile.tsx (its original and still main caller) so
 * RequiredTwoFactorSetup can reuse the exact same enrollment flow for the
 * mandatory front-loaded setup a b2b_admin hits right after verify-email —
 * see LLMR-b2b-self-signup's first-login deadlock fix. `dismissible` and
 * `cancelLabel` exist for that caller: mandatory setup has no "just close
 * this and come back later" — cancelling there means signing back out.
 */
export function TwoFactorEnrollModal({
  method,
  onClose,
  onConfirmed,
  dismissible = true,
  cancelLabel,
}: {
  method: components['schemas']['TwoFactorMethod'];
  onClose: () => void;
  onConfirmed: (recoveryCodes: string[]) => void;
  dismissible?: boolean;
  cancelLabel?: string;
}) {
  const { dict } = useLanguage();
  const isOTP = method !== 'totp';
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [destinationMasked, setDestinationMasked] = useState<string | null>(
    null,
  );
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  async function start(isResend: boolean) {
    setSendBusy(true);
    setStartError(null);
    if (isResend) setResendMessage(null);
    try {
      const res = await api.enrollTwoFactor(method);
      setEnrollmentId(res.enrollment_id);
      if (method === 'totp') {
        setSecret(res.totp_secret ?? null);
        if (res.totp_uri)
          setQrDataUrl(
            await toDataURL(res.totp_uri, { margin: 1, width: 220 }),
          );
      } else {
        setDestinationMasked(res.destination_masked ?? null);
        if (isResend) setResendMessage(dict.profile.twoFactorResendSent);
      }
    } catch (e) {
      setStartError(
        e instanceof ApiError && e.is('/problems/conflict')
          ? dict.profile.errTwoFactorAlreadyEnrolled
          : isOTP && e instanceof ApiError && e.status === 503
            ? dict.profile.errTwoFactorSendFailed
            : dict.profile.errTwoFactorGeneric,
      );
    } finally {
      setSendBusy(false);
    }
  }

  useEffect(() => {
    void start(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    if (!enrollmentId || code.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.confirmTwoFactorEnrollment(
        enrollmentId,
        code.trim(),
      );
      onConfirmed(res.recovery_codes);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 401
          ? dict.profile.errTwoFactorCode
          : dict.profile.errTwoFactorGeneric,
      );
    } finally {
      setBusy(false);
    }
  }

  const label = dict.profile.methodName(method);

  return (
    <Modal
      title={dict.profile.twoFactorModalTitleFor(label)}
      onClose={onClose}
      dismissible={dismissible}
    >
      {startError && !enrollmentId ? (
        <p className="field-error">{startError}</p>
      ) : (
        <form onSubmit={(e) => void confirm(e)}>
          {method === 'totp' ? (
            <>
              <p className="note">{dict.profile.twoFactorScanLede}</p>
              {qrDataUrl && (
                <div className="totp-qr-box">
                  <img src={qrDataUrl} alt="" width={220} height={220} />
                </div>
              )}
              {secret && (
                <div className="secret-box">
                  <code>{secret}</code>
                </div>
              )}
            </>
          ) : (
            <p className="note">
              {sendBusy && !destinationMasked
                ? dict.profile.twoFactorSendingCode
                : destinationMasked
                  ? dict.profile.twoFactorOTPLede(destinationMasked)
                  : dict.profile.twoFactorSendingCode}
            </p>
          )}

          <label id="totp-confirm-code-label" htmlFor="totp-confirm-code">
            {method === 'totp'
              ? dict.profile.twoFactorCodeLabel
              : dict.profile.twoFactorOTPCodeLabel}
          </label>
          <OtpInput
            id="totp-confirm-code"
            value={code}
            onChange={setCode}
            autoFocus
            aria-invalid={Boolean(error)}
            aria-labelledby="totp-confirm-code-label"
            digitAriaLabel={dict.common.otpDigitLabel}
          />
          {error && <p className="field-error">{error}</p>}
          {isOTP && startError && enrollmentId && (
            <p className="field-error">{startError}</p>
          )}

          {isOTP && (
            <OtpResendPrompt
              prompt={dict.common.otpDidntGetCode}
              actionLabel={dict.common.otpResendLink}
              busyLabel={dict.profile.twoFactorSendingCode}
              busy={sendBusy}
              sentMessage={resendMessage}
              onResend={() => void start(true)}
            />
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="ghost"
              onClick={onClose}
              disabled={busy}
            >
              {cancelLabel ?? dict.common.cancel}
            </button>
            <button
              type="submit"
              disabled={busy || !enrollmentId || !isOtpComplete(code)}
            >
              {busy
                ? dict.profile.twoFactorConfirming
                : dict.profile.twoFactorConfirmButton}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/**
 * Shows freshly minted recovery codes exactly once, right after enrollment
 * confirms. Not dismissible without acknowledging — same rule as the API key
 * secret reveal (see Modal's own doc comment): this is the one chance to
 * copy these down.
 */
export function RecoveryCodesReveal({
  codes,
  onDismiss,
}: {
  codes: string[];
  onDismiss: () => void;
}) {
  const { dict } = useLanguage();
  const [acknowledged, setAcknowledged] = useState(false);
  const { copied, copy } = useCopyToClipboard();

  return (
    <Modal title={dict.profile.twoFactorRecoveryModalTitle} dismissible={false}>
      <p className="warn-text">
        <strong>{dict.profile.twoFactorRecoveryWarning}</strong>{' '}
        {dict.profile.twoFactorRecoveryWarningBody}
      </p>

      <div className="recovery-codes-grid">
        {codes.map((c) => (
          <code key={c}>{c}</code>
        ))}
      </div>
      <button
        type="button"
        className="ghost"
        onClick={() => void copy(codes.join('\n'))}
      >
        {copied
          ? dict.profile.twoFactorRecoveryCopied
          : dict.profile.twoFactorRecoveryCopyAll}
      </button>

      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        {dict.profile.twoFactorRecoveryAckLabel}
      </label>

      <div className="modal-actions">
        <button type="button" disabled={!acknowledged} onClick={onDismiss}>
          {dict.profile.twoFactorRecoveryDone}
        </button>
      </div>
    </Modal>
  );
}
